import type { SurveyPolygon } from "@/features/polygon/types";
import type { Map as MapboxMap } from "mapbox-gl";

const SOURCE_PREFIX = "survey-polygon-source-";
const FILL_LAYER_PREFIX = "survey-polygon-fill-";
const LINE_LAYER_PREFIX = "survey-polygon-line-";
const LABEL_LAYER_PREFIX = "survey-polygon-label-";

const getSourceId = (polygonId: string) => `${SOURCE_PREFIX}${polygonId}`;
const getFillLayerId = (polygonId: string) => `${FILL_LAYER_PREFIX}${polygonId}`;
const getLineLayerId = (polygonId: string) => `${LINE_LAYER_PREFIX}${polygonId}`;
const getLabelLayerId = (polygonId: string) => `${LABEL_LAYER_PREFIX}${polygonId}`;

const safeOpacity = (value: number | undefined, fallback: number): number => {
  if (typeof value !== "number" || Number.isNaN(value)) return fallback;
  return Math.max(0, Math.min(1, value));
};

const safeWidth = (value: number | undefined, fallback: number): number => {
  if (typeof value !== "number" || Number.isNaN(value)) return fallback;
  return Math.max(0, value);
};

const removePolygonById = (map: MapboxMap, polygonId: string): void => {
  const labelLayerId = getLabelLayerId(polygonId);
  const lineLayerId = getLineLayerId(polygonId);
  const fillLayerId = getFillLayerId(polygonId);
  const sourceId = getSourceId(polygonId);

  if (map.getLayer(labelLayerId)) {
    map.removeLayer(labelLayerId);
  }
  if (map.getLayer(lineLayerId)) {
    map.removeLayer(lineLayerId);
  }
  if (map.getLayer(fillLayerId)) {
    map.removeLayer(fillLayerId);
  }
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }
};

const buildFeature = (polygon: SurveyPolygon): GeoJSON.Feature<GeoJSON.Polygon> => ({
  type: "Feature",
  properties: {
    polygonId: polygon.polygonId,
    name: polygon.name,
  },
  geometry: {
    type: "Polygon",
    coordinates: polygon.coordinates,
  },
});

export const removeAllPolygons = (
  map: MapboxMap,
  polygonIds: string[],
): void => {
  polygonIds.forEach((polygonId) => removePolygonById(map, polygonId));
};

export const addPolygonsToMap = (
  map: MapboxMap,
  polygons: SurveyPolygon[],
): string[] => {
  const visiblePolygons = polygons.filter((polygon) => !polygon.isHidden);

  visiblePolygons.forEach((polygon) => {
    removePolygonById(map, polygon.polygonId);

    const sourceId = getSourceId(polygon.polygonId);
    const fillLayerId = getFillLayerId(polygon.polygonId);
    const lineLayerId = getLineLayerId(polygon.polygonId);
    const labelLayerId = getLabelLayerId(polygon.polygonId);

    const feature = buildFeature(polygon);

    map.addSource(sourceId, {
      type: "geojson",
      data: feature,
    });

    map.addLayer({
      id: fillLayerId,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": polygon.style.fillColor ?? "#1f7a8c",
        "fill-opacity": safeOpacity(polygon.style.fillOpacity, 0.28),
      },
    });

    map.addLayer({
      id: lineLayerId,
      type: "line",
      source: sourceId,
      paint: {
        "line-color": polygon.style.borderColor ?? "#1f7a8c",
        "line-opacity": safeOpacity(polygon.style.borderOpacity, 1),
        "line-width": safeWidth(polygon.style.borderWidth, 2),
        ...(polygon.style.borderDasharray?.length
          ? { "line-dasharray": polygon.style.borderDasharray }
          : {}),
      },
    });

    // Add label layer for polygon name if showLabel is true
    if (polygon.showLabel !== false) {
      map.addLayer({
        id: labelLayerId,
        type: "symbol",
        source: sourceId,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": 14,
          "text-anchor": "center",
          "text-allow-overlap": false,
          "text-ignore-placement": false,
        },
        paint: {
          "text-color": "#000000",
          "text-halo-color": "#ffffff",
          "text-halo-width": 2,
          "text-halo-blur": 1,
        },
      });
    }
  });

  return visiblePolygons.map((polygon) => polygon.polygonId);
};
